
import { useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/logger";

type SettingsCache = {
  [key: string]: {
    value: string;
    timestamp: number;
  }
};

// Reduced cache time to ensure more frequent updates for important settings
const CACHE_TTL = 30 * 1000; // 30 seconds cache

export function useSystemSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const settingsCache = useRef<SettingsCache>({});
  
  // Function to get a setting by key with caching - admin/developer only
  const getSetting = useCallback(async (key: string, defaultValue: string = ""): Promise<string> => {
    // Check cache first
    const cachedSetting = settingsCache.current[key];
    const now = Date.now();
    
    if (cachedSetting && (now - cachedSetting.timestamp < CACHE_TTL)) {
      console.log(`Using cached setting for ${key}: ${cachedSetting.value}`);
      return cachedSetting.value;
    }
    
    setIsLoading(true);
    try {
      console.log(`Fetching setting: ${key}`);
      const { data, error } = await supabase.rpc('fetch_setting', { setting_key: key });
      
      if (error) {
        console.error(`Error loading ${key} setting:`, error);
        logError(`Error loading ${key} setting`, "useSystemSettings.ts", { error });
        return defaultValue;
      }
      
      const value = data || defaultValue;
      console.log(`Setting value retrieved: ${value}`);
      
      // Update cache
      settingsCache.current[key] = {
        value,
        timestamp: now
      };
      
      return value;
    } catch (error) {
      console.error(`Error fetching ${key} setting:`, error);
      logError(`Error fetching ${key} setting`, "useSystemSettings.ts", { error });
      return defaultValue;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to get a public setting by key with caching - available to all users
  const getPublicSetting = useCallback(async (key: string, defaultValue: string = ""): Promise<string> => {
    // Check cache first
    const cachedSetting = settingsCache.current[key];
    const now = Date.now();
    
    if (cachedSetting && (now - cachedSetting.timestamp < CACHE_TTL)) {
      console.log(`Using cached public setting for ${key}: ${cachedSetting.value}`);
      return cachedSetting.value;
    }
    
    setIsLoading(true);
    try {
      console.log(`Fetching public setting: ${key}`);
      const { data, error } = await supabase.rpc('fetch_public_setting', { setting_key: key });
      
      if (error) {
        console.error(`Error loading public ${key} setting:`, error);
        logError(`Error loading public ${key} setting`, "useSystemSettings.ts", { error });
        return defaultValue;
      }
      
      const value = data || defaultValue;
      console.log(`Public setting value retrieved: ${value}`);
      
      // Update cache
      settingsCache.current[key] = {
        value,
        timestamp: now
      };
      
      return value;
    } catch (error) {
      console.error(`Error fetching public ${key} setting:`, error);
      logError(`Error fetching public ${key} setting`, "useSystemSettings.ts", { error });
      return defaultValue;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Function to get a boolean setting with caching
  const getBooleanSetting = useCallback(async (key: string, defaultValue: boolean = false): Promise<boolean> => {
    const stringValue = await getSetting(key, defaultValue.toString());
    return stringValue.toLowerCase() === 'true';
  }, [getSetting]);
  
  // Function to get a public boolean setting with caching
  const getPublicBooleanSetting = useCallback(async (key: string, defaultValue: boolean = false): Promise<boolean> => {
    const stringValue = await getPublicSetting(key, defaultValue.toString());
    return stringValue.toLowerCase() === 'true';
  }, [getPublicSetting]);
  
  // Function to update a setting
  const updateSetting = useCallback(async (key: string, value: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      console.log(`Updating setting: ${key} to value: ${value}`);
      
      const { error } = await supabase.rpc('update_setting', { 
        setting_key: key, 
        setting_value: value 
      });
      
      if (error) {
        console.error(`Error saving ${key} setting:`, error);
        logError(`Error saving ${key} setting`, "useSystemSettings.ts", { error });
        return false;
      }
      
      // Update cache immediately
      settingsCache.current[key] = {
        value,
        timestamp: Date.now()
      };
      
      console.log(`Setting updated successfully: ${key}`);
      return true;
    } catch (error) {
      console.error(`Error saving ${key} setting:`, error);
      logError(`Error saving ${key} setting`, "useSystemSettings.ts", { error });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Function to update a boolean setting
  const updateBooleanSetting = useCallback(async (key: string, value: boolean): Promise<boolean> => {
    return updateSetting(key, value.toString());
  }, [updateSetting]);
  
  // Function to clear cache
  const clearCache = useCallback(() => {
    console.log("Clearing settings cache");
    settingsCache.current = {};
  }, []);
  
  return {
    getSetting,
    getPublicSetting,
    getBooleanSetting,
    getPublicBooleanSetting,
    updateSetting,
    updateBooleanSetting,
    clearCache,
    isLoading
  };
}
